import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";

import { getTrialDaysRemaining, type TimeUntilDate } from "./utils.ts";

import { useActiveOrganization, useIsOrgOwner } from "@/hooks/organizations";
import { useOrganizationSubscription } from "@/queries/organizations";
import { LOCAL_STORAGE } from "@/constants";
import {
  OrganizationSubscription,
  OrganizationSubscriptionResponse,
} from "@/types/organization-usage";
import { capitalize } from "@/utils/strings";
import { hasPaymentIssue } from "@/utils/subscriptionStatus";

interface OrganizationSubscriptionContextValue {
  subscriptionStatus: OrganizationSubscriptionResponse | undefined;
  isLoadingSubscriptionStatus: boolean;
  isOrgOwner: boolean;
  subscription: OrganizationSubscription | undefined;
  planName: string | undefined;
  isPLAdmin: boolean;

  isTrialPlan: boolean;
  isFreePlan: boolean;
  isNewEnterprisePlan: boolean;
  isCanceled: boolean;
  status: string | null;
  hasStripe: boolean;
  hasLegacySubscription: boolean;
  hasActiveSubscription: boolean;
  legacyPlanName: string;
  isScheduledPlanChange: boolean;
  canceledAt: string | null;
  scheduledPlanType: string | undefined;
  currentPeriodEnd: string | null;
  currentPeriodStart: string | null;
  trialEndsAt: string | null;
  timeUntilCancellation: TimeUntilDate | null;
  timeUntilPlanChange: TimeUntilDate | null;
  stripe_subscription_status: string | null;
  hasPaymentIssue: boolean;
}

const OrganizationSubscriptionContext = createContext<OrganizationSubscriptionContextValue | null>(
  null,
);

export const PL_ADMIN_PLAN_NAME = "PL Admin";
export const PL_ADMIN_PLAN_TYPE = "pl_admin";

export enum SubscriptionPlanType {
  FREE = "free",
  PRO = "pro",
  TEAM = "team",
  ENTERPRISE = "enterprise",
}

export enum StripeSubscriptionStatus {
  ACTIVE = "active",
  TRIALING = "trialing",
  PAST_DUE = "past_due",
  INCOMPLETE = "incomplete",
  CANCELED = "canceled",
  UNPAID = "unpaid",
}

export enum SubscriptionStatusDisplay {
  ACTIVE = "Active",
  TRIAL = "Trial",
  PAYMENT_PAST_DUE = "Payment Past Due",
  PAYMENT_INCOMPLETE = "Payment Incomplete",
  CANCELED = "Canceled",
  PAYMENT_UNPAID = "Payment Unpaid",
}

const STRIPE_STATUS_TO_DISPLAY_MAP: Record<StripeSubscriptionStatus, SubscriptionStatusDisplay> = {
  [StripeSubscriptionStatus.ACTIVE]: SubscriptionStatusDisplay.ACTIVE,
  [StripeSubscriptionStatus.TRIALING]: SubscriptionStatusDisplay.TRIAL,
  [StripeSubscriptionStatus.PAST_DUE]: SubscriptionStatusDisplay.PAYMENT_PAST_DUE,
  [StripeSubscriptionStatus.INCOMPLETE]: SubscriptionStatusDisplay.PAYMENT_INCOMPLETE,
  [StripeSubscriptionStatus.CANCELED]: SubscriptionStatusDisplay.CANCELED,
  [StripeSubscriptionStatus.UNPAID]: SubscriptionStatusDisplay.PAYMENT_UNPAID,
};

export const mapStripeSubscriptionStatusToStatus = (status: string): string => {
  const stripeStatus = status as StripeSubscriptionStatus;
  return STRIPE_STATUS_TO_DISPLAY_MAP[stripeStatus] ?? "";
};

export const OrganizationSubscriptionProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: subscriptionStatus, isLoading: isLoadingSubscriptionStatus } =
    useOrganizationSubscription();
  const { data: activeOrganization } = useActiveOrganization();
  const activeOrganizationId = activeOrganization?.id;
  const queryClient = useQueryClient();

  const isOrgOwner = useIsOrgOwner();

  const subscription = subscriptionStatus?.subscription;

  useEffect(() => {
    queryClient.removeQueries({
      queryKey: ["organization_subscription"],
    });
  }, [activeOrganizationId, queryClient]);

  useEffect(() => {
    if (subscription?.plan_type && !isLoadingSubscriptionStatus) {
      const posthogPlanType = localStorage.getItem(LOCAL_STORAGE.POSTHOG_PLAN_TYPE);
      const isTrial = subscription.status === StripeSubscriptionStatus.TRIALING;
      let planType = subscription.plan_type;

      if (isTrial) {
        planType = "team_trial";
      }

      if (posthogPlanType !== planType) {
        posthog.people.set({ ["subscription_plan_type"]: planType });
        localStorage.setItem(LOCAL_STORAGE.POSTHOG_PLAN_TYPE, planType);
      }
    }
  }, [
    subscription?.plan_type,
    isLoadingSubscriptionStatus,
    subscription?.id,
    subscription?.status,
  ]);

  const rawPlanName = useMemo(() => subscription?.plan_type || "Free", [subscription?.plan_type]);

  const canceledAt = subscription?.canceled_at || null;
  const currentPeriodEnd = subscription?.current_period_end || null;
  const currentPeriodStart = subscription?.current_period_start || null;
  const trialEndsAt = subscription?.trial_ends_at || null;

  const isScheduledPlanChange = useMemo(
    () => !!subscription?.is_scheduled_plan_change,
    [subscription?.is_scheduled_plan_change],
  );

  const scheduledPlanTypeRaw = useMemo(
    () => subscription?.scheduled_plan_type,
    [subscription?.scheduled_plan_type],
  );

  const isNewEnterprisePlan = useMemo(
    () => rawPlanName?.toLowerCase() === SubscriptionPlanType.ENTERPRISE,
    [rawPlanName],
  );

  const isTrialPlan = useMemo(
    () => subscription?.status === StripeSubscriptionStatus.TRIALING,
    [subscription?.status],
  );

  const isCanceled = useMemo(
    () => subscription?.stripe_subscription_status === StripeSubscriptionStatus.CANCELED,
    [subscription?.stripe_subscription_status],
  );

  const hasStripe = useMemo(
    () => !!(subscription?.stripe_subscription_id && subscription?.stripe_customer_id),
    [subscription?.stripe_subscription_id, subscription?.stripe_customer_id],
  );

  const isFreePlan = useMemo(
    () => subscription?.plan_type === SubscriptionPlanType.FREE,
    [subscription?.plan_type],
  );

  const hasActiveSubscription = useMemo(
    () =>
      subscription?.status === StripeSubscriptionStatus.ACTIVE &&
      (subscription?.plan_type !== SubscriptionPlanType.FREE || !subscription),
    [subscription],
  );

  const hasLegacySubscription = useMemo(() => false, []);

  const displayPlanName = useMemo(() => {
    if (rawPlanName) {
      return capitalize(rawPlanName);
    }

    return undefined;
  }, [rawPlanName]);

  const legacyPlanName = useMemo(() => "", []);

  const timeUntilCancellation: TimeUntilDate | null = useMemo(
    () => (canceledAt && currentPeriodEnd ? getTrialDaysRemaining(currentPeriodEnd) : null),
    [canceledAt, currentPeriodEnd],
  );

  const timeUntilPlanChange: TimeUntilDate | null = useMemo(
    () =>
      isScheduledPlanChange && currentPeriodEnd ? getTrialDaysRemaining(currentPeriodEnd) : null,
    [isScheduledPlanChange, currentPeriodEnd],
  );

  const formattedScheduledPlanType = useMemo(
    () => (scheduledPlanTypeRaw ? capitalize(scheduledPlanTypeRaw) : undefined),
    [scheduledPlanTypeRaw],
  );

  const isPLAdmin = useMemo(
    () => subscription?.plan_type === PL_ADMIN_PLAN_TYPE,
    [subscription?.plan_type],
  );

  const planName = isPLAdmin ? PL_ADMIN_PLAN_NAME : displayPlanName;

  const stripeSubscriptionStatus = useMemo(
    () => subscription?.stripe_subscription_status ?? null,
    [subscription?.stripe_subscription_status],
  );

  const hasPaymentIssueValue = useMemo(
    () => hasPaymentIssue(stripeSubscriptionStatus),
    [stripeSubscriptionStatus],
  );

  const value: OrganizationSubscriptionContextValue = {
    subscriptionStatus,
    isLoadingSubscriptionStatus,
    isOrgOwner,
    subscription,
    planName,
    isPLAdmin,
    isTrialPlan,
    isFreePlan,
    isNewEnterprisePlan,
    isCanceled,
    status: subscription?.status ?? null,
    hasStripe,
    hasLegacySubscription,
    hasActiveSubscription,
    legacyPlanName,
    isScheduledPlanChange,
    canceledAt,
    scheduledPlanType: formattedScheduledPlanType,
    currentPeriodEnd,
    currentPeriodStart,
    trialEndsAt,
    timeUntilCancellation,
    timeUntilPlanChange,
    stripe_subscription_status: stripeSubscriptionStatus,
    hasPaymentIssue: hasPaymentIssueValue,
  };

  return (
    <OrganizationSubscriptionContext.Provider value={value}>
      {children}
    </OrganizationSubscriptionContext.Provider>
  );
};

export const useOrganizationSubscriptionData = () => {
  const context = useContext(OrganizationSubscriptionContext);
  if (!context) {
    throw new Error(
      "useOrganizationSubscriptionData must be used within OrganizationSubscriptionProvider",
    );
  }
  return context;
};
